import React, {
    useCallback,
    useEffect,
    useMemo,
    useState,
} from "react";

import {
    Alert,
    Avatar,
    Badge,
    Button,
    Card,
    Col,
    Descriptions,
    Empty,
    Form,
    Input,
    Modal,
    Pagination,
    Popconfirm,
    Row,
    Select,
    Space,
    Statistic,
    Table,
    Tag,
    Tooltip,
    Typography,
    message,
} from "antd";

import {
    CheckCircleOutlined,
    CloseCircleOutlined,
    DeleteOutlined,
    EditOutlined,
    EnvironmentOutlined,
    MobileOutlined,
    ReloadOutlined,
    ScanOutlined,
    SearchOutlined,
    StopOutlined,
    TabletOutlined,
    WifiOutlined,
} from "@ant-design/icons";

import api from "../../api/api";
import { useAcademicYear } from "../../AcademicYearContext";




const {
    Title,
    Text,
} = Typography;


/*
|--------------------------------------------------------------------------
| API
|--------------------------------------------------------------------------
*/

const DEVICES_ENDPOINT =
    "/api/smart-teacher-attendance/devices/";


/*
|--------------------------------------------------------------------------
| PAGE SETTINGS
|--------------------------------------------------------------------------
*/

const PAGE_SIZE = 10;


/*
|--------------------------------------------------------------------------
| COMPONENT
|--------------------------------------------------------------------------
*/

const TeacherScannerDeviceManagement = () => {

    /*
    |--------------------------------------------------------------------------
    | ACTIVE ACADEMIC YEAR
    |--------------------------------------------------------------------------
    */

    const {
        selectedYear,
    } = useAcademicYear();


    /*
    |--------------------------------------------------------------------------
    | STATE
    |--------------------------------------------------------------------------
    */

    const [devices, setDevices] =
        useState([]);

    const [loading, setLoading] =
        useState(false);

    const [submitting, setSubmitting] =
        useState(false);

    const [error, setError] =
        useState(null);

    const [searchText, setSearchText] =
        useState("");

    const [statusFilter, setStatusFilter] =
        useState("all");

    const [page, setPage] =
        useState(1);

    const [total, setTotal] =
        useState(0);


    /*
    |--------------------------------------------------------------------------
    | MODALS
    |--------------------------------------------------------------------------
    */

    const [formModalOpen, setFormModalOpen] =
        useState(false);

    const [detailsModalOpen, setDetailsModalOpen] =
        useState(false);


    /*
    |--------------------------------------------------------------------------
    | EDIT MODE
    |--------------------------------------------------------------------------
    */

    const [editingDevice, setEditingDevice] =
        useState(null);

    const [selectedDevice, setSelectedDevice] =
        useState(null);


    /*
    |--------------------------------------------------------------------------
    | FORM
    |--------------------------------------------------------------------------
    */

    const [deviceForm] =
        Form.useForm();


    /*
    |--------------------------------------------------------------------------
    | FETCH DEVICES
    |--------------------------------------------------------------------------
    */

    const fetchDevices = useCallback(
        async () => {

            /*
            |--------------------------------------------------------------------------
            | Respect active academic year.
            |--------------------------------------------------------------------------
            */

            if (!selectedYear?.id) {

                setDevices([]);

                setTotal(0);

                return;

            }


            setLoading(true);

            setError(null);


            try {

                const params = {

                    academic_year:
                        selectedYear.id,

                    page,

                    page_size:
                        PAGE_SIZE,

                };


                if (
                    searchText.trim()
                ) {

                    params.search =
                        searchText.trim();

                }


                if (
                    statusFilter !== "all"
                ) {

                    params.is_active =
                        statusFilter ===
                        "active";

                }


                const response =
                    await api.get(
                        DEVICES_ENDPOINT,
                        {
                            params,
                        }
                    );


                const data =
                    response.data || {};


                /*
                |--------------------------------------------------------------------------
                | DRF PAGINATION
                |--------------------------------------------------------------------------
                */

                if (
                    Array.isArray(
                        data.results
                    )
                ) {

                    setDevices(
                        data.results
                    );

                    setTotal(
                        data.count || 0
                    );

                }

                /*
                |--------------------------------------------------------------------------
                | Non-paginated response
                |--------------------------------------------------------------------------
                */

                else if (
                    Array.isArray(
                        data
                    )
                ) {

                    setDevices(
                        data
                    );

                    setTotal(
                        data.length
                    );

                }

                else {

                    setDevices([]);

                    setTotal(0);

                }

            } catch (requestError) {

                console.error(
                    "Failed to load scanner devices:",
                    requestError
                );


                const errorMessage =
                    requestError
                        ?.response
                        ?.data
                        ?.detail ||
                    requestError
                        ?.response
                        ?.data
                        ?.message ||
                    "Unable to load scanner devices.";


                setError(
                    errorMessage
                );

            } finally {

                setLoading(false);

            }

        },
        [
            selectedYear?.id,
            page,
            searchText,
            statusFilter,
        ]
    );


    /*
    |--------------------------------------------------------------------------
    | LOAD
    |--------------------------------------------------------------------------
    */

    useEffect(() => {

        fetchDevices();

    }, [
        fetchDevices,
    ]);


    /*
    |--------------------------------------------------------------------------
    | RESET PAGE WHEN FILTER CHANGES
    |--------------------------------------------------------------------------
    */

    useEffect(() => {

        setPage(1);

    }, [
        searchText,
        statusFilter,
        selectedYear?.id,
    ]);


    /*
    |--------------------------------------------------------------------------
    | STATISTICS
    |--------------------------------------------------------------------------
    */

    const statistics =
        useMemo(() => {

            const active =
                devices.filter(
                    device =>
                        device.is_active ===
                        true
                ).length;


            const inactive =
                devices.filter(
                    device =>
                        device.is_active !==
                        true
                ).length;


            const totalScans =
                devices.reduce(
                    (
                        total,
                        device
                    ) =>
                        total +
                        Number(
                            device.total_scans ||
                            0
                        ),
                    0
                );


            const online =
                devices.filter(
                    device =>
                        device.last_seen &&
                        (
                            Date.now() -
                            new Date(
                                device.last_seen
                            ).getTime()
                        ) <=
                        5 * 60 * 1000
                ).length;


            return {
                active,
                inactive,
                totalScans,
                online,
            };

        }, [
            devices,
        ]);


    /*
    |--------------------------------------------------------------------------
    | OPEN CREATE MODAL
    |--------------------------------------------------------------------------
    */

    const openCreateModal =
        () => {

            setEditingDevice(
                null
            );

            deviceForm.resetFields();

            setFormModalOpen(
                true
            );

        };


    /*
    |--------------------------------------------------------------------------
    | OPEN EDIT MODAL
    |--------------------------------------------------------------------------
    */

    const openEditModal =
        (
            device
        ) => {

            setEditingDevice(
                device
            );


            deviceForm.setFieldsValue({

                device_name:
                    device.device_name,

                device_id:
                    device.device_id,

                gate_name:
                    device.gate_name,

                location_description:
                    device.location_description,

            });


            setFormModalOpen(
                true
            );

        };


    /*
    |--------------------------------------------------------------------------
    | CLOSE FORM
    |--------------------------------------------------------------------------
    */

    const closeFormModal =
        () => {

            if (
                submitting
            ) {
                return;
            }


            setFormModalOpen(
                false
            );

            setEditingDevice(
                null
            );

            deviceForm.resetFields();

        };


    /*
    |--------------------------------------------------------------------------
    | SAVE DEVICE
    |--------------------------------------------------------------------------
    */

    const handleSaveDevice =
        async () => {

            try {

                const values =
                    await deviceForm.validateFields();


                if (
                    !selectedYear?.id
                ) {

                    message.error(
                        "No active academic year is selected."
                    );

                    return;

                }


                setSubmitting(true);


                /*
                |--------------------------------------------------------------------------
                | CREATE
                |--------------------------------------------------------------------------
                */

                if (
                    !editingDevice
                ) {

                    const payload = {

                        device_name:
                            values.device_name.trim(),

                        device_id:
                            values.device_id.trim(),

                        gate_name:
                            values.gate_name.trim(),

                        location_description:
                            values.location_description?.trim() ||
                            null,

                        academic_year:
                            selectedYear.id,

                    };


                    await api.post(
                        DEVICES_ENDPOINT,
                        payload
                    );


                    message.success(
                        "Scanner device registered successfully."
                    );

                }

                /*
                |--------------------------------------------------------------------------
                | UPDATE
                |--------------------------------------------------------------------------
                */

                else {

                    const payload = {

                        device_name:
                            values.device_name.trim(),

                        gate_name:
                            values.gate_name.trim(),

                        location_description:
                            values.location_description?.trim() ||
                            null,

                    };


                    await api.patch(
                        `${DEVICES_ENDPOINT}${editingDevice.id}/`,
                        payload
                    );


                    message.success(
                        "Scanner device updated successfully."
                    );

                }


                closeFormModal();

                await fetchDevices();

            } catch (requestError) {

                /*
                |--------------------------------------------------------------------------
                | Ant Design validation
                |--------------------------------------------------------------------------
                */

                if (
                    requestError?.errorFields
                ) {

                    return;

                }


                console.error(
                    "Failed to save scanner device:",
                    requestError
                );


                message.error(
                    requestError
                        ?.response
                        ?.data
                        ?.detail ||
                    requestError
                        ?.response
                        ?.data
                        ?.message ||
                    "Unable to save scanner device."
                );

            } finally {

                setSubmitting(false);

            }

        };


    /*
    |--------------------------------------------------------------------------
    | TOGGLE DEVICE STATUS
    |--------------------------------------------------------------------------
    */

    const toggleDeviceStatus =
        async (
            device
        ) => {

            try {

                setSubmitting(true);


                await api.patch(
                    `${DEVICES_ENDPOINT}${device.id}/`,
                    {
                        is_active:
                            !device.is_active,
                    }
                );


                message.success(
                    device.is_active
                        ? "Scanner device deactivated."
                        : "Scanner device activated."
                );


                await fetchDevices();


                if (
                    selectedDevice?.id ===
                    device.id
                ) {

                    setSelectedDevice(
                        previous => ({
                            ...previous,
                            is_active:
                                !device.is_active,
                        })
                    );

                }

            } catch (requestError) {

                console.error(
                    "Failed to change device status:",
                    requestError
                );


                message.error(
                    requestError
                        ?.response
                        ?.data
                        ?.detail ||
                    "Unable to change scanner status."
                );

            } finally {

                setSubmitting(false);

            }

        };


    /*
    |--------------------------------------------------------------------------
    | DELETE DEVICE
    |--------------------------------------------------------------------------
    |
    | In production, the backend should ideally prevent deletion
    | when the device has historical scan logs.
    |
    */

    const deleteDevice =
        async (
            device
        ) => {

            try {

                setSubmitting(true);


                await api.delete(
                    `${DEVICES_ENDPOINT}${device.id}/`
                );


                message.success(
                    "Scanner device removed successfully."
                );


                if (
                    selectedDevice?.id ===
                    device.id
                ) {

                    setDetailsModalOpen(
                        false
                    );

                    setSelectedDevice(
                        null
                    );

                }


                await fetchDevices();

            } catch (requestError) {

                console.error(
                    "Failed to delete scanner device:",
                    requestError
                );


                message.error(
                    requestError
                        ?.response
                        ?.data
                        ?.detail ||
                    "This scanner device cannot be removed."
                );

            } finally {

                setSubmitting(false);

            }

        };


    /*
    |--------------------------------------------------------------------------
    | VIEW DETAILS
    |--------------------------------------------------------------------------
    */

    const openDetails =
        (
            device
        ) => {

            setSelectedDevice(
                device
            );

            setDetailsModalOpen(
                true
            );

        };


    /*
    |--------------------------------------------------------------------------
    | FORMAT DATE
    |--------------------------------------------------------------------------
    */

    const formatDate =
        (
            value
        ) => {

            if (!value) {

                return (
                    <Text
                        type="secondary"
                    >
                        Never
                    </Text>
                );

            }


            return new Date(
                value
            ).toLocaleString();

        };


    /*
    |--------------------------------------------------------------------------
    | ONLINE STATUS
    |--------------------------------------------------------------------------
    */

    const isDeviceOnline =
        (
            device
        ) => {

            if (
                !device?.last_seen
            ) {

                return false;

            }


            const difference =
                Date.now() -
                new Date(
                    device.last_seen
                ).getTime();


            return (
                difference <=
                5 * 60 * 1000
            );

        };


    /*
    |--------------------------------------------------------------------------
    | DEVICE TYPE ICON
    |--------------------------------------------------------------------------
    */

    const deviceIcon =
        (
            device
        ) => {

            const name =
                (
                    device?.device_name ||
                    ""
                ).toLowerCase();


            if (
                name.includes(
                    "tablet"
                )
            ) {

                return (
                    <TabletOutlined />
                );

            }


            return (
                <MobileOutlined />
            );

        };


    /*
    |--------------------------------------------------------------------------
    | STATUS
    |--------------------------------------------------------------------------
    */

    const renderStatus =
        (
            device
        ) => {

            if (
                device.is_active
            ) {

                return (

                    <Tag
                        color="success"
                        icon={
                            <CheckCircleOutlined />
                        }
                    >
                        Active
                    </Tag>

                );

            }


            return (

                <Tag
                    color="error"
                    icon={
                        <StopOutlined />
                    }
                >
                    Inactive
                </Tag>

            );

        };


    /*
    |--------------------------------------------------------------------------
    | ONLINE BADGE
    |--------------------------------------------------------------------------
    */

    const renderOnline =
        (
            device
        ) => {

            const online =
                isDeviceOnline(
                    device
                );


            if (
                online
            ) {

                return (

                    <Badge
                        status="success"
                        text={
                            <Text
                                style={{
                                    color:
                                        "#389e0d",
                                    fontWeight:
                                        600,
                                }}
                            >
                                Online
                            </Text>
                        }
                    />

                );

            }


            return (

                <Badge
                    status="default"
                    text={
                        <Text
                            type="secondary"
                        >
                            Offline
                        </Text>
                    }
                />

            );

        };


    /*
    |--------------------------------------------------------------------------
    | TABLE COLUMNS
    |--------------------------------------------------------------------------
    */

    const columns = [

        {
            title: "Scanner",

            key: "scanner",

            render: (
                _,
                device
            ) => (

                <Space>

                    <Avatar
                        size={44}
                        style={{
                            background:
                                device.is_active
                                    ? "#f97316"
                                    : "#6b7280",
                        }}
                        icon={
                            deviceIcon(
                                device
                            )
                        }
                    />


                    <div>

                        <Text
                            strong
                        >
                            {
                                device.device_name
                            }
                        </Text>


                        <br />

                        <Text
                            type="secondary"
                            style={{
                                fontSize:
                                    12,
                            }}
                        >
                            ID:{" "}
                            {
                                device.device_id
                            }
                        </Text>

                    </div>

                </Space>

            ),
        },


        {
            title: "Gate / Location",

            key: "location",

            render: (
                _,
                device
            ) => (

                <div>

                    <Space
                        size={5}
                    >

                        <EnvironmentOutlined />

                        <Text
                            strong
                        >
                            {
                                device.gate_name ||
                                "--"
                            }
                        </Text>

                    </Space>


                    {device.location_description && (

                        <>

                            <br />

                            <Text
                                type="secondary"
                                style={{
                                    fontSize:
                                        12,
                                }}
                            >
                                {
                                    device.location_description
                                }
                            </Text>

                        </>

                    )}

                </div>

            ),
        },


        {
            title: "Status",

            key: "status",

            render: (
                _,
                device
            ) => (

                <Space
                    direction="vertical"
                    size={2}
                >

                    {renderStatus(
                        device
                    )}

                    {renderOnline(
                        device
                    )}

                </Space>

            ),
        },


        {
            title: "Last Seen",

            dataIndex:
                "last_seen",

            key:
                "last_seen",

            render:
                formatDate,
        },


        {
            title: "Last Scan",

            dataIndex:
                "last_scan_at",

            key:
                "last_scan_at",

            render:
                formatDate,
        },


        {
            title: "Total Scans",

            dataIndex:
                "total_scans",

            key:
                "total_scans",

            render:
                value => (

                    <Text
                        strong
                    >
                        {Number(
                            value || 0
                        ).toLocaleString()}
                    </Text>

                ),
        },


        {
            title: "Actions",

            key: "actions",

            fixed: "right",

            width: 220,

            render: (
                _,
                device
            ) => (

                <Space
                    wrap
                >

                    <Tooltip
                        title="View details"
                    >

                        <Button
                            onClick={() =>
                                openDetails(
                                    device
                                )
                            }
                        >
                            View
                        </Button>

                    </Tooltip>


                    <Tooltip
                        title="Edit scanner"
                    >

                        <Button
                            icon={
                                <EditOutlined />
                            }
                            onClick={() =>
                                openEditModal(
                                    device
                                )
                            }
                        />

                    </Tooltip>


                    <Tooltip
                        title={
                            device.is_active
                                ? "Deactivate"
                                : "Activate"
                        }
                    >

                        <Popconfirm
                            title={
                                device.is_active
                                    ? "Deactivate this scanner?"
                                    : "Activate this scanner?"
                            }
                            description={
                                device.is_active
                                    ? "This device will no longer be allowed to process attendance scans."
                                    : "This device will be allowed to process attendance scans again."
                            }
                            okText={
                                device.is_active
                                    ? "Deactivate"
                                    : "Activate"
                            }
                            cancelText="Cancel"
                            onConfirm={() =>
                                toggleDeviceStatus(
                                    device
                                )
                            }
                        >

                            <Button
                                danger={
                                    device.is_active
                                }
                                icon={
                                    device.is_active
                                        ? (
                                            <StopOutlined />
                                        )
                                        : (
                                            <CheckCircleOutlined />
                                        )
                                }
                            />

                        </Popconfirm>

                    </Tooltip>


                    {!Number(
                        device.total_scans || 0
                    ) && (

                        <Tooltip
                            title="Remove device"
                        >

                            <Popconfirm
                                title="Remove scanner device?"
                                description={
                                    "This device has no recorded scans."
                                }
                                okText="Remove"
                                cancelText="Cancel"
                                okButtonProps={{
                                    danger:
                                        true,
                                }}
                                onConfirm={() =>
                                    deleteDevice(
                                        device
                                    )
                                }
                            >

                                <Button
                                    danger
                                    icon={
                                        <DeleteOutlined />
                                    }
                                />

                            </Popconfirm>

                        </Tooltip>

                    )}

                </Space>

            ),
        },

    ];


    /*
    |--------------------------------------------------------------------------
    | NO ACTIVE YEAR
    |--------------------------------------------------------------------------
    */

    if (
        !selectedYear
    ) {

        return (

            <div
                style={{
                    padding:
                        20,
                }}
            >

                <Alert
                    type="warning"
                    showIcon
                    message={
                        "No Active Academic Year"
                    }
                    description={
                        "Scanner device management requires an active academic year."
                    }
                />

            </div>

        );

    }


    /*
    |--------------------------------------------------------------------------
    | MAIN PAGE
    |--------------------------------------------------------------------------
    */

    return (

        <div
            style={{
                minHeight:
                    "100vh",

                background:
                    "#f3f4f6",

                padding:
                    16,
            }}
        >

            <div
                style={{
                    maxWidth:
                        1400,

                    margin:
                        "0 auto",
                }}
            >

                {/* ================================================= */}
                {/* HEADER */}
                {/* ================================================= */}

                <Card
                    bordered={false}
                    style={{
                        borderRadius:
                            16,

                        marginBottom:
                            16,
                    }}
                >

                    <Row
                        gutter={[
                            16,
                            16,
                        ]}
                        align="middle"
                        justify="space-between"
                    >

                        <Col
                            xs={24}
                            md={15}
                        >

                            <Space
                                align="start"
                                size={14}
                            >

                                <div
                                    style={{
                                        width:
                                            52,

                                        height:
                                            52,

                                        borderRadius:
                                            12,

                                        background:
                                            "#f97316",

                                        display:
                                            "flex",

                                        alignItems:
                                            "center",

                                        justifyContent:
                                            "center",

                                        flexShrink:
                                            0,
                                    }}
                                >

                                    <MobileOutlined
                                        style={{
                                            color:
                                                "#fff",

                                            fontSize:
                                                28,
                                        }}
                                    />

                                </div>


                                <div>

                                    <Title
                                        level={2}
                                        style={{
                                            margin:
                                                0,

                                            color:
                                                "#111827",
                                        }}
                                    >
                                        Scanner Devices
                                    </Title>


                                    <Text
                                        type="secondary"
                                    >
                                        Register and manage
                                        Android phones and
                                        tablets authorized
                                        to scan Teacher
                                        Smart Cards.
                                    </Text>

                                </div>

                            </Space>

                        </Col>


                        <Col
                            xs={24}
                            md={9}
                        >

                            <Space
                                wrap
                                style={{
                                    width:
                                        "100%",

                                    justifyContent:
                                        "flex-end",
                                }}
                            >

                                <Tag
                                    color="orange"
                                >
                                    Academic Year:{" "}
                                    {
                                        selectedYear.name
                                    }
                                </Tag>


                                <Button
                                    icon={
                                        <ReloadOutlined />
                                    }
                                    loading={
                                        loading
                                    }
                                    onClick={
                                        fetchDevices
                                    }
                                >
                                    Refresh
                                </Button>


                                <Button
                                    type="primary"
                                    icon={
                                        <MobileOutlined />
                                    }
                                    onClick={
                                        openCreateModal
                                    }
                                    style={{
                                        background:
                                            "#f97316",

                                        borderColor:
                                            "#f97316",

                                        fontWeight:
                                            600,
                                    }}
                                >
                                    Register Scanner
                                </Button>

                            </Space>

                        </Col>

                    </Row>

                </Card>


                {/* ================================================= */}
                {/* ERROR */}
                {/* ================================================= */}

                {error && (

                    <Alert
                        type="error"
                        showIcon
                        closable
                        message={
                            error
                        }
                        style={{
                            marginBottom:
                                16,
                        }}
                        onClose={() =>
                            setError(
                                null
                            )
                        }
                    />

                )}


                {/* ================================================= */}
                {/* STATISTICS */}
                {/* ================================================= */}

                <Row
                    gutter={[
                        16,
                        16,
                    ]}
                    style={{
                        marginBottom:
                            16,
                    }}
                >

                    <Col
                        xs={12}
                        sm={6}
                    >

                        <Card
                            bordered={false}
                        >

                            <Statistic
                                title="Registered"
                                value={
                                    total
                                }
                                prefix={
                                    <MobileOutlined />
                                }
                            />

                        </Card>

                    </Col>


                    <Col
                        xs={12}
                        sm={6}
                    >

                        <Card
                            bordered={false}
                        >

                            <Statistic
                                title="Active"
                                value={
                                    statistics.active
                                }
                                prefix={
                                    <CheckCircleOutlined
                                        style={{
                                            color:
                                                "#52c41a",
                                        }}
                                    />
                                }
                            />

                        </Card>

                    </Col>


                    <Col
                        xs={12}
                        sm={6}
                    >

                        <Card
                            bordered={false}
                        >

                            <Statistic
                                title="Online"
                                value={
                                    statistics.online
                                }
                                prefix={
                                    <WifiOutlined
                                        style={{
                                            color:
                                                "#52c41a",
                                        }}
                                    />
                                }
                            />

                        </Card>

                    </Col>


                    <Col
                        xs={12}
                        sm={6}
                    >

                        <Card
                            bordered={false}
                        >

                            <Statistic
                                title="Total Scans"
                                value={
                                    statistics.totalScans
                                }
                                prefix={
                                    <ScanOutlined
                                        style={{
                                            color:
                                                "#f97316",
                                        }}
                                    />
                                }
                            />

                        </Card>

                    </Col>

                </Row>


                {/* ================================================= */}
                {/* SEARCH / FILTER */}
                {/* ================================================= */}

                <Card
                    bordered={false}
                    style={{
                        marginBottom:
                            16,

                        borderRadius:
                            16,
                    }}
                >

                    <Row
                        gutter={[
                            12,
                            12,
                        ]}
                    >

                        <Col
                            xs={24}
                            md={11}
                            lg={9}
                        >

                            <Input
                                size="large"
                                allowClear
                                prefix={
                                    <SearchOutlined />
                                }
                                placeholder={
                                    "Search device, ID, gate or location..."
                                }
                                value={
                                    searchText
                                }
                                onChange={
                                    event =>
                                        setSearchText(
                                            event
                                                .target
                                                .value
                                        )
                                }
                            />

                        </Col>


                        <Col
                            xs={24}
                            sm={12}
                            md={6}
                        >

                            <Select
                                size="large"
                                style={{
                                    width:
                                        "100%",
                                }}
                                value={
                                    statusFilter
                                }
                                onChange={
                                    setStatusFilter
                                }
                            >

                                <Select.Option
                                    value="all"
                                >
                                    All Devices
                                </Select.Option>

                                <Select.Option
                                    value="active"
                                >
                                    Active Only
                                </Select.Option>

                                <Select.Option
                                    value="inactive"
                                >
                                    Inactive Only
                                </Select.Option>

                            </Select>

                        </Col>


                        <Col
                            xs={24}
                            sm={12}
                            md={6}
                        >

                            <Button
                                size="large"
                                block
                                onClick={() => {

                                    setSearchText(
                                        ""
                                    );

                                    setStatusFilter(
                                        "all"
                                    );

                                    setPage(
                                        1
                                    );

                                }}
                            >
                                Reset Filters
                            </Button>

                        </Col>

                    </Row>

                </Card>


                {/* ================================================= */}
                {/* TABLE */}
                {/* ================================================= */}

                <Card
                    bordered={false}
                    style={{
                        borderRadius:
                            16,
                    }}
                >

                    <Table
                        rowKey="id"
                        columns={
                            columns
                        }
                        dataSource={
                            devices
                        }
                        loading={
                            loading
                        }
                        pagination={
                            false
                        }
                        scroll={{
                            x: 1100,
                        }}
                        locale={{
                            emptyText: (
                                <Empty
                                    description={
                                        "No scanner devices found."
                                    }
                                />
                            ),
                        }}
                    />


                    {total > 0 && (

                        <div
                            style={{
                                display:
                                    "flex",

                                justifyContent:
                                    "flex-end",

                                marginTop:
                                    20,
                            }}
                        >

                            <Pagination
                                current={
                                    page
                                }
                                pageSize={
                                    PAGE_SIZE
                                }
                                total={
                                    total
                                }
                                showSizeChanger={
                                    false
                                }
                                showTotal={
                                    value =>
                                        `${value} scanner devices`
                                }
                                onChange={
                                    setPage
                                }
                            />

                        </div>

                    )}

                </Card>

            </div>


            {/* ===================================================== */}
            {/* CREATE / EDIT MODAL */}
            {/* ===================================================== */}

            <Modal
                open={
                    formModalOpen
                }
                title={
                    <Space>

                        <MobileOutlined
                            style={{
                                color:
                                    "#f97316",
                            }}
                        />

                        <span>
                            {
                                editingDevice
                                    ? "Edit Scanner Device"
                                    : "Register Scanner Device"
                            }
                        </span>

                    </Space>
                }
                onCancel={closeFormModal}
                onOk={
                    handleSaveDevice
                }
                okText={
                    editingDevice
                        ? "Save Changes"
                        : "Register Device"
                }
                confirmLoading={
                    submitting
                }
                okButtonProps={{
                    style: {
                        background:
                            "#f97316",

                        borderColor:
                            "#f97316",
                    },
                }}
                destroyOnHidden
            >

                {!editingDevice && (

                    <Alert
                        type="info"
                        showIcon
                        message={
                            "Register the Android scanner before using it."
                        }
                        description={
                            "The device ID should uniquely identify this physical phone or tablet. Do not use the teacher's ID or Smart Card number as the device ID."
                        }
                        style={{
                            marginBottom:
                                20,
                        }}
                    />

                )}


                <Form
                    form={
                        deviceForm
                    }
                    layout="vertical"
                >

                    {/* ============================================= */}
                    {/* DEVICE NAME */}
                    {/* ============================================= */}

                    <Form.Item
                        label="Device Name"
                        name="device_name"
                        rules={[
                            {
                                required:
                                    true,

                                message:
                                    "Enter a device name.",
                            },

                            {
                                max:
                                    100,

                                message:
                                    "Device name cannot exceed 100 characters.",
                            },
                        ]}
                    >

                        <Input
                            prefix={
                                <MobileOutlined />
                            }
                            placeholder={
                                "Example: Main Gate Android"
                            }
                            maxLength={
                                100
                            }
                            showCount
                        />

                    </Form.Item>


                    {/* ============================================= */}
                    {/* DEVICE ID */}
                    {/* ============================================= */}

                    <Form.Item
                        label="Device ID"
                        name="device_id"
                        rules={[
                            {
                                required:
                                    true,

                                message:
                                    "Enter the device ID.",
                            },

                            {
                                max:
                                    150,

                                message:
                                    "Device ID cannot exceed 150 characters.",
                            },
                        ]}
                        extra={
                            editingDevice
                                ? "The device ID should normally not be changed after registration."
                                : "Use a unique identifier generated by your scanner application."
                        }
                    >

                        <Input
                            disabled={
                                Boolean(
                                    editingDevice
                                )
                            }
                            placeholder={
                                "Example: ANDROID-MAIN-GATE-001"
                            }
                            maxLength={
                                150
                            }
                        />

                    </Form.Item>


                    {/* ============================================= */}
                    {/* GATE */}
                    {/* ============================================= */}

                    <Form.Item
                        label="Gate Name"
                        name="gate_name"
                        rules={[
                            {
                                required:
                                    true,

                                message:
                                    "Enter the gate name.",
                            },

                            {
                                max:
                                    100,

                                message:
                                    "Gate name cannot exceed 100 characters.",
                            },
                        ]}
                    >

                        <Input
                            prefix={
                                <EnvironmentOutlined />
                            }
                            placeholder={
                                "Example: Main Gate"
                            }
                            maxLength={
                                100
                            }
                            showCount
                        />

                    </Form.Item>


                    {/* ============================================= */}
                    {/* LOCATION */}
                    {/* ============================================= */}

                    <Form.Item
                        label="Location Description"
                        name="location_description"
                    >

                        <Input.TextArea
                            rows={3}
                            placeholder={
                                "Example: Security office beside the main entrance."
                            }
                            maxLength={
                                255
                            }
                            showCount
                        />

                    </Form.Item>


                    {/* ============================================= */}
                    {/* ACADEMIC YEAR */}
                    {/* ============================================= */}

                    {!editingDevice && (

                        <Alert
                            type="warning"
                            showIcon
                            message={
                                `Active Academic Year: ${selectedYear.name}`
                            }
                            description={
                                "This scanner registration is being created under the currently selected academic year."
                            }
                        />

                    )}

                </Form>

            </Modal>


            {/* ===================================================== */}
            {/* DETAILS MODAL */}
            {/* ===================================================== */}

            <Modal
                open={
                    detailsModalOpen
                }
                title={
                    <Space>

                        <MobileOutlined
                            style={{
                                color:
                                    "#f97316",
                            }}
                        />

                        <span>
                            Scanner Device Details
                        </span>

                    </Space>
                }
                footer={null}
                onCancel={() => {

                    setDetailsModalOpen(
                        false
                    );

                    setSelectedDevice(
                        null
                    );

                }}
                width={650}
            >

                {selectedDevice && (

                    <>

                        {/* ========================================= */}
                        {/* DEVICE HEADER */}
                        {/* ========================================= */}

                        <Card
                            bordered={false}
                            style={{
                                background:
                                    "#111827",

                                borderRadius:
                                    14,

                                marginBottom:
                                    20,
                            }}
                        >

                            <Space
                                align="center"
                                size={15}
                            >

                                <Avatar
                                    size={64}
                                    style={{
                                        background:
                                            "#f97316",

                                        fontSize:
                                            28,
                                    }}
                                    icon={
                                        deviceIcon(
                                            selectedDevice
                                        )
                                    }
                                />


                                <div>

                                    <Title
                                        level={3}
                                        style={{
                                            margin:
                                                0,

                                            color:
                                                "#fff",
                                        }}
                                    >
                                        {
                                            selectedDevice.device_name
                                        }
                                    </Title>


                                    <Text
                                        style={{
                                            color:
                                                "#d1d5db",
                                        }}
                                    >
                                        {
                                            selectedDevice.gate_name
                                        }
                                    </Text>


                                    <div
                                        style={{
                                            marginTop:
                                                8,
                                        }}
                                    >

                                        {renderOnline(
                                            selectedDevice
                                        )}

                                    </div>

                                </div>

                            </Space>

                        </Card>


                        {/* ========================================= */}
                        {/* DETAILS */}
                        {/* ========================================= */}

                        <Descriptions
                            bordered
                            column={{
                                xs: 1,
                                sm: 2,
                            }}
                        >

                            <Descriptions.Item
                                label="Device Name"
                            >
                                {
                                    selectedDevice.device_name ||
                                    "--"
                                }
                            </Descriptions.Item>


                            <Descriptions.Item
                                label="Status"
                            >
                                {renderStatus(
                                    selectedDevice
                                )}
                            </Descriptions.Item>


                            <Descriptions.Item
                                label="Device ID"
                                span={2}
                            >

                                <Text
                                    code
                                    copyable
                                >
                                    {
                                        selectedDevice.device_id ||
                                        "--"
                                    }
                                </Text>

                            </Descriptions.Item>


                            <Descriptions.Item
                                label="Gate"
                            >
                                {
                                    selectedDevice.gate_name ||
                                    "--"
                                }
                            </Descriptions.Item>


                            <Descriptions.Item
                                label="Academic Year"
                            >
                                {
                                    selectedYear.name
                                }
                            </Descriptions.Item>


                            <Descriptions.Item
                                label="Location"
                                span={2}
                            >
                                {
                                    selectedDevice.location_description ||
                                    "--"
                                }
                            </Descriptions.Item>


                            <Descriptions.Item
                                label="Last Seen"
                            >
                                {
                                    formatDate(
                                        selectedDevice.last_seen
                                    )
                                }
                            </Descriptions.Item>


                            <Descriptions.Item
                                label="Last Scan"
                            >
                                {
                                    formatDate(
                                        selectedDevice.last_scan_at
                                    )
                                }
                            </Descriptions.Item>


                            <Descriptions.Item
                                label="Total Scans"
                            >

                                <Text
                                    strong
                                >
                                    {Number(
                                        selectedDevice.total_scans ||
                                        0
                                    ).toLocaleString()}
                                </Text>

                            </Descriptions.Item>


                            <Descriptions.Item
                                label="Registered"
                            >
                                {
                                    formatDate(
                                        selectedDevice.created_at
                                    )
                                }
                            </Descriptions.Item>

                        </Descriptions>


                        {/* ========================================= */}
                        {/* ACTIONS */}
                        {/* ========================================= */}

                        <Space
                            wrap
                            style={{
                                marginTop:
                                    20,
                            }}
                        >

                            <Button
                                icon={
                                    <EditOutlined />
                                }
                                onClick={() => {

                                    setDetailsModalOpen(
                                        false
                                    );

                                    openEditModal(
                                        selectedDevice
                                    );

                                }}
                            >
                                Edit Device
                            </Button>


                            <Popconfirm
                                title={
                                    selectedDevice.is_active
                                        ? "Deactivate this scanner?"
                                        : "Activate this scanner?"
                                }
                                description={
                                    selectedDevice.is_active
                                        ? "The Android device will no longer be able to process teacher attendance."
                                        : "The Android device will be allowed to process teacher attendance again."
                                }
                                okText={
                                    selectedDevice.is_active
                                        ? "Deactivate"
                                        : "Activate"
                                }
                                cancelText="Cancel"
                                onConfirm={() =>
                                    toggleDeviceStatus(
                                        selectedDevice
                                    )
                                }
                            >

                                <Button
                                    danger={
                                        selectedDevice.is_active
                                    }
                                    icon={
                                        selectedDevice.is_active
                                            ? (
                                                <StopOutlined />
                                            )
                                            : (
                                                <CheckCircleOutlined />
                                            )
                                    }
                                >
                                    {
                                        selectedDevice.is_active
                                            ? "Deactivate"
                                            : "Activate"
                                    }
                                </Button>

                            </Popconfirm>


                            {!Number(
                                selectedDevice.total_scans ||
                                0
                            ) && (

                                <Popconfirm
                                    title={
                                        "Remove this scanner?"
                                    }
                                    description={
                                        "This action permanently removes the device registration."
                                    }
                                    okText="Remove"
                                    cancelText="Cancel"
                                    okButtonProps={{
                                        danger:
                                            true,
                                    }}
                                    onConfirm={() =>
                                        deleteDevice(
                                            selectedDevice
                                        )
                                    }
                                >

                                    <Button
                                        danger
                                        icon={
                                            <DeleteOutlined />
                                        }
                                    >
                                        Remove
                                    </Button>

                                </Popconfirm>

                            )}

                        </Space>

                    </>

                )}

            </Modal>


            {/* ===================================================== */}
            {/* RESPONSIVE STYLES */}
            {/* ===================================================== */}

            <style>
                {`

                .ant-table-wrapper {
                    width: 100%;
                }


                .ant-table-cell {
                    white-space: nowrap;
                }


                @media (max-width: 576px) {

                    .ant-card-body {
                        padding: 14px !important;
                    }


                    .ant-statistic-title {
                        font-size: 12px !important;
                    }


                    .ant-statistic-content {
                        font-size: 20px !important;
                    }


                    .ant-modal {
                        max-width:
                            calc(100vw - 20px);
                    }


                    .ant-modal-content {
                        padding: 16px !important;
                    }


                    .ant-descriptions-item-label,
                    .ant-descriptions-item-content {
                        font-size: 13px !important;
                    }

                }

                `}
            </style>

        </div>

    );

};


export default TeacherScannerDeviceManagement;